/**
 * Inventory panel (C-wheel → INVENTORY): the WORN strip, a slot grid over the game,
 * the suit/ship chips and the fabricator. Interactions are deliberately mouse-simple —
 * click to pick up & place (move/merge/swap), right-click to use a consumable / install
 * a module / WEAR a piece of gear, and click a worn slot to take it off.
 *
 * Worn gear does not sit in the bag (v49): equipping frees the square, which is the
 * feature. The standard-issue weapon picker still lives on the YOU screen; Z draws and
 * stows whatever resolves as equipped.
 *
 * All mutations go through the shared helpers; the panel re-renders whole
 * (33 nodes — cheap) and reports changes for persistence.
 */
import {
  ITEMS, INV_SLOTS, STYLE_PARTS, moveItem, RECIPES, canCraft, craft, countItem, equipSlotFor,
  type EquipSlot,
} from "@hyperlight/shared";
import type { PlayerRpg } from "../rpg/rpg";

/** The slots the paper-doll shows, in reading order. `offhand` is deliberately absent —
 *  nothing can occupy it yet (the shield is a body construct, not an item). */
const DOLL_SLOTS: readonly EquipSlot[] = [...STYLE_PARTS, "mainhand"];

/** Short labels for the worn strip; the full item name is the button's body. */
const SLOT_LABELS: Record<string, string> = {
  head: "HEAD", torso: "TORSO", arms: "ARMS", legs: "LEGS", feet: "FEET", mainhand: "WEAPON", offhand: "OFFHAND",
};

export class InventoryPanel {
  visible = false;
  private el: HTMLElement;
  private grid: HTMLElement;
  private doll: HTMLElement;
  private craftEl: HTMLElement;
  private healthEl: HTMLElement;
  private creditsEl: HTMLElement;
  private selected: number | null = null;

  constructor(
    private rpg: PlayerRpg,
    private handlers: {
      onChange: () => void;
      /** Mirror of every local mutation — the server-authoritative intent. */
      onIntent: (m: {
        op: "move" | "use" | "equip" | "craft";
        a?: number;
        b?: number;
        eq?: "primary" | "secondary";
        recipe?: string;
      }) => void;
      onClose: () => void;
      /** SUIT chip: hand off to the outfit editor (in the void). */
      onOutfit: () => void;
      /** GARAGE chip: hand off to the chassis picker. */
      onGarage: () => void;
      /** Right-clicked a ship module: install it (consumes on success). */
      onInstall: (slot: number) => void;
      /** Right-clicked wearable gear / a weapon: put it on (frees the bag square). */
      onEquip: (slot: number) => void;
      /** Clicked a worn slot: take it off (refuses if the bag is full). */
      onUnequip: (slot: EquipSlot) => void;
      /** SHIPYARD chip: hand off to the ship builder (in the void). */
      onShipyard: () => void;
    },
  ) {
    this.el = document.createElement("div");
    this.el.id = "inventory";
    this.el.innerHTML = `
      <div class="inv-card">
        <h2>INVENTORY</h2>
        <div class="inv-health">SUIT INTEGRITY <span id="inv-hp"></span></div>
        <div class="inv-credits">CREDITS <span id="inv-cr"></span></div>
        <div class="inv-equip">
          <button class="inv-eq" data-eq="suit">OUTFIT<span>open locker</span></button>
          <button class="inv-eq" data-eq="garage">GARAGE<span>pick a ride</span></button>
          <button class="inv-eq" data-eq="ship">SHIPYARD<span>edit ship</span></button>
        </div>
        <h3 class="inv-craft-title">WORN</h3>
        <div class="inv-doll"></div>
        <div class="inv-grid"></div>
        <h3 class="inv-craft-title">FABRICATOR</h3>
        <div class="inv-craft"></div>
        <div class="inv-hint">click: move &nbsp;·&nbsp; right-click: use / wear &nbsp;·&nbsp; worn: stow &nbsp;·&nbsp; esc</div>
      </div>
    `;
    this.el.style.display = "none";
    document.body.appendChild(this.el);
    this.grid = this.el.querySelector(".inv-grid")!;
    this.doll = this.el.querySelector(".inv-doll")!;
    this.doll.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest(".inv-worn") as HTMLElement | null;
      if (!btn || !btn.dataset.slot || btn.classList.contains("inv-worn-empty")) return;
      this.handlers.onUnequip(btn.dataset.slot as EquipSlot);
    });
    this.craftEl = this.el.querySelector(".inv-craft")!;
    this.craftEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest(".craft-btn") as HTMLElement | null;
      if (!btn) return;
      const r = RECIPES[Number(btn.dataset.r)];
      if (r && craft(this.rpg.inventory, r)) {
        this.handlers.onIntent({ op: "craft", recipe: r.out.id });
        this.rpg.rev++;
        this.handlers.onChange();
        this.render();
      }
    });
    this.healthEl = this.el.querySelector("#inv-hp")!;
    this.creditsEl = this.el.querySelector("#inv-cr")!;

    this.grid.addEventListener("click", (e) => this.onSlotClick(e, false));
    this.grid.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.onSlotClick(e, true);
    });
    this.el.querySelector('[data-eq="suit"]')!.addEventListener("click", () => {
      this.close();
      this.handlers.onOutfit();
    });
    this.el.querySelector('[data-eq="garage"]')!.addEventListener("click", () => {
      this.close();
      this.handlers.onGarage();
    });
    this.el.querySelector('[data-eq="ship"]')!.addEventListener("click", () => {
      this.close();
      this.handlers.onShipyard();
    });
  }

  open(): void {
    this.visible = true;
    this.selected = null;
    this.el.style.display = "flex";
    this.render();
  }

  close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.el.style.display = "none";
    this.handlers.onClose();
  }

  toggle(): void {
    if (this.visible) this.close();
    else this.open();
  }

  private slotIndex(e: Event): number | null {
    const btn = (e.target as HTMLElement).closest(".inv-slot");
    if (!btn) return null;
    return Number((btn as HTMLElement).dataset.i);
  }

  private onSlotClick(e: Event, use: boolean): void {
    const i = this.slotIndex(e);
    if (i === null) return;
    const inv = this.rpg.inventory;
    if (use) {
      const item = inv.slots[i];
      const def = item ? ITEMS[item.id] : null;
      if (def && def.kind === "module") this.handlers.onInstall(i);
      // Wearable gear and real weapons go ON rather than being consumed.
      else if (def && equipSlotFor(def)) this.handlers.onEquip(i);
      // Keycards / registrations (v49): the CLAIM is server-side (it writes the
      // collection ledger first), so send the intent with NO optimistic consume —
      // a refused duplicate keeps the item, and the invState echo reconciles.
      else if (def && (def.kind === "vehicle" || def.kind === "ship")) {
        this.handlers.onIntent({ op: "use", a: i });
      }
      else if (this.rpg.useConsumable(i)) {
        this.handlers.onIntent({ op: "use", a: i });
        this.handlers.onChange();
      }
      this.selected = null;
    } else if (this.selected === null) {
      if (inv.slots[i]) this.selected = i;
    } else {
      moveItem(inv, this.selected, i);
      this.handlers.onIntent({ op: "move", a: this.selected, b: i });
      this.rpg.rev++;
      this.selected = null;
      this.handlers.onChange();
    }
    this.render();
  }

  /** Re-render from state (call when rpg.rev changes while visible). */
  render(): void {
    const inv = this.rpg.inventory;
    const h = this.rpg.health;
    this.healthEl.textContent = `${Math.round(h.hp)} / ${h.max}`;
    this.creditsEl.textContent = `${this.rpg.credits.toLocaleString()} CR`;

    // WORN strip. A filled slot is clickable (take it off); an empty one is a
    // placeholder so the row reads as a body, not a list that grows and shrinks.
    this.doll.innerHTML = DOLL_SLOTS.map((slot) => {
      const id = inv.equipped[slot];
      const def = id ? ITEMS[id] : null;
      const label = SLOT_LABELS[slot] ?? slot.toUpperCase();
      // Nine cells share the card's width, so the body has ~53px. For gear that is
      // the SET name ("Drifter") — the slot label already says the noun, and
      // "Drifter Pauldrons" would not fit. Weapons keep their real name.
      const worn = def?.gear ? def.gear.variant : def?.name ?? "";
      return def
        ? `<button class="inv-worn inv-${def.kind}" data-slot="${slot}" title="${def.name} — ${def.desc}&#10;click to stow"><i>${label}</i><b>${worn}</b></button>`
        : `<button class="inv-worn inv-worn-empty" data-slot="${slot}" disabled><i>${label}</i><b>—</b></button>`;
    }).join("");

    let html = "";
    for (let i = 0; i < INV_SLOTS; i++) {
      const s = inv.slots[i];
      const def = s ? ITEMS[s.id] : null;
      const cls = [
        "inv-slot",
        def ? `inv-${def.kind}` : "inv-empty",
        this.selected === i ? "selected" : "",
      ].join(" ");
      // Soulbound stacks wear a corner badge — the one visible difference
      // between a tradeable copy and one that has been worn.
      html += def
        ? `<button class="${cls}" data-i="${i}" title="${def.desc}${s!.b === 1 ? "&#10;SOULBOUND — cannot be traded or sold" : ""}${s!.q ? `&#10;GRADE Q${s!.q}` : ""}"><b>${def.name}</b>${
            s!.n > 1 ? `<i>×${s!.n}</i>` : ""
          }${s!.b === 1 ? `<i class="inv-badge inv-bound">◈</i>` : ""}${s!.q ? `<i class="inv-badge inv-quality inv-q${s!.q}">Q${s!.q}</i>` : ""}</button>`
        : `<button class="${cls}" data-i="${i}"></button>`;
    }
    this.grid.innerHTML = html;

    // Fabricator: every recipe, ingredients colored by availability.
    let chtml = "";
    RECIPES.forEach((r, ri) => {
      const out = ITEMS[r.out.id];
      const ok = canCraft(inv, r);
      const ings = r.in
        .map((ing) => {
          const have = countItem(inv, ing.id);
          const good = have >= ing.n;
          return `<i class="${good ? "have" : "lack"}">${ITEMS[ing.id].name} ${have}/${ing.n}</i>`;
        })
        .join(" ");
      chtml += `<div class="craft-row${ok ? "" : " craft-no"}">
        <b title="${out.desc}">${out.name}${r.out.n > 1 ? ` ×${r.out.n}` : ""}</b>
        <span>${ings}</span>
        <button class="craft-btn" data-r="${ri}" ${ok ? "" : "disabled"}>CRAFT</button>
      </div>`;
    });
    this.craftEl.innerHTML = chtml;
  }
}
